import { alertPassesMute, type AlertKind } from "../chat/alert.ts";
import { InterfaceSound, PlayerSound, SOUND_MANIFEST, SoundActor, parseSoundManifest, playerCombatHook, soundHooksOfRules } from "odyssey-shared";
import { PAGE_RULES } from "../rules.ts";
import { readAudioSettings, writeBackgroundMute, writeWhisperBackground, writeLevel, type AudioSettings, type LevelId, type VolumeLevel } from "./audio-settings.ts";
import { SoundBank } from "./bank.ts";
import { SoundCues } from "./cues.ts";
import { browserCanPlay, browserContext, browserMedia, type ContextLike, type MediaLike } from "./graph.ts";
import { SoundMixer } from "./mixer.ts";
import { MusicDirector, type MusicScene } from "./music.ts";
import { createAudioReport, type AudioReport } from "./report.ts";

/**
 * The game page's sound (ADR-085): the bank, the mixer, the music and the cues, the settings read once and kept as they
 * change, and the page's wiring: the first press unlocks the context, the document's visibility drives the background
 * mute, a press on a button, menu item, option or tab clicks, and the manifest is read from the asset base.
 */

/**
 * The controls a press on which clicks.
 */
const CLICKABLE = "button:not(:disabled), [role='menuitem']:not([aria-disabled='true']), [role='option']:not([aria-disabled='true']), [role='tab']:not([aria-disabled='true']), input[type='checkbox']:not(:disabled)";

/**
 * The game menu's surfaces from the world outward, by how deeply each is nested.
 */
const WINDOW_DEPTH: readonly string[] = ["closed", "menu", "settings"];

/**
 * What the page's sound is made from: the browser's, or a test's stand-ins.
 */
export interface PageSoundParts {
  readonly context: () => ContextLike | null;
  readonly media: () => MediaLike | null;
  readonly canPlay: (type: string) => boolean;
  readonly fetchBytes: (url: string) => Promise<ArrayBuffer>;
  readonly fetchJson: (url: string) => Promise<unknown>;
  readonly random: () => number;
  readonly settings: AudioSettings;
  readonly keep: { readonly level: (id: LevelId, level: VolumeLevel) => void; readonly background: (mute: boolean) => void; readonly whispers?: (through: boolean) => void };
}

/**
 * The page's sound.
 */
export class PageSound {
  public readonly report: AudioReport = createAudioReport();
  public readonly bank: SoundBank;
  public readonly mixer: SoundMixer;
  public readonly music: MusicDirector;
  public readonly cues: SoundCues;

  /**
   * @param parts What the sound is made from.
   */
  public constructor(private readonly parts: PageSoundParts) {
    const fallbacks = new Map<string, string>();
    for (const hook of soundHooksOfRules(PAGE_RULES, [])) {
      if (hook.fallback !== null) {
        fallbacks.set(hook.hook, hook.fallback);
      }
    }
    this.bank = new SoundBank(parts.fetchBytes, parts.canPlay, fallbacks, parts.random, () => {
      this.report.decoded += 1;
    });
    this.mixer = new SoundMixer(parts.context, this.bank, parts.settings, this.report);
    this.report.levels = () => {
      const levels: Record<string, { volume: number; muted: boolean }> = {};
      for (const [id, level] of Object.entries(this.mixer.current.levels)) {
        levels[id] = { volume: level.volume, muted: level.muted };
      }
      return levels;
    };
    this.music = new MusicDirector({
      context: () => this.mixer.audio,
      input: (channel) => this.mixer.input(channel),
      media: parts.media,
      urlOf: (track) => this.bank.urlOf(track),
      random: parts.random,
      onTrack: (title) => {
        this.report.music = title;
      },
    });
    this.mixer.onUnlock(() => this.music.unlocked());
    this.mixer.onQuiet((silent) => this.music.silence(silent));
    this.cues = new SoundCues(
      (hook, actor, dx, dy, owner) => this.mixer.play(hook, actor, dx, dy, owner),
      PAGE_RULES,
      (x, y) => this.mixer.listenerAt(x, y),
    );
  }

  /**
   * Reads the manifest from the asset base; a manifest that is missing or malformed leaves the page silent and is
   * logged.
   *
   * @param base The asset base, `/dist` or the CDN's origin.
   * @returns A promise that settles once the manifest is read or given up.
   */
  public async load(base: string): Promise<void> {
    try {
      const manifest = parseSoundManifest(await this.parts.fetchJson(`${base}/${SOUND_MANIFEST}`));
      this.bank.setManifest(manifest, base);
      const context = this.mixer.audio;
      if (context !== null) {
        void this.bank.load(context);
      }
      this.music.setManifest(manifest);
    } catch (error) {
      console.warn(`the sound manifest at ${base}/${SOUND_MANIFEST} did not load; the page plays no sound`, error);
    }
  }

  /**
   * Asks for a screen's music.
   *
   * @param scene The scene.
   */
  public scene(scene: MusicScene): void {
    this.music.scene(scene);
  }

  /**
   * Sets and keeps Master's or a channel's level.
   *
   * @param id Master or a channel.
   * @param level The level.
   */
  public setLevel(id: LevelId, level: VolumeLevel): void {
    this.mixer.setLevel(id, level);
    this.parts.keep.level(id, level);
  }

  /**
   * Sets Master's or a channel's level without keeping it, as a slider moves.
   *
   * @param id Master or a channel.
   * @param level The level.
   */
  public previewLevel(id: LevelId, level: VolumeLevel): void {
    this.mixer.setLevel(id, level);
  }

  /**
   * Sets and keeps whether sound stops while the window is in the background.
   *
   * @param mute True to stop it.
   */
  public setBackgroundMute(mute: boolean): void {
    this.mixer.setBackgroundMute(mute);
    this.parts.keep.background(mute);
  }

  /**
   * Sets and keeps whether a whisper's alert sounds while the window is in the background and muted.
   *
   * @param through True to let it sound.
   */
  public setWhisperBackground(through: boolean): void {
    this.mixer.setWhisperBackground(through);
    this.parts.keep.whispers?.(through);
  }

  /**
   * Sounds the chat alert (ADR-085's amendments "the chat alert" and of 2026-10-13). In front, and in the background
   * while sound plays there, it plays as any interface sound. While the background mute holds, a whisper's alert is
   * played through it, alone, when the viewer's switch is on; a mention's, and a whisper's with the switch off,
   * stay silent.
   *
   * @param kind What the alert is for.
   * @returns True when the alert sounded, or was asked of a context resumed for it; false when it stayed silent.
   */
  public chatAlert(kind: AlertKind): boolean {
    if (!this.mixer.silent) {
      return this.mixer.play(InterfaceSound.ChatAlert, SoundActor.Own);
    }
    return alertPassesMute(kind, this.mixer.current.whisperBackground) && this.mixer.playThrough(InterfaceSound.ChatAlert, SoundActor.Own);
  }

  /**
   * The settings in force.
   *
   * @returns The settings.
   */
  public get settings(): AudioSettings {
    return this.mixer.current;
  }

  /**
   * Plays an interface sound.
   *
   * @param hook One of `InterfaceSound`.
   */
  public ui(hook: (typeof InterfaceSound)[keyof typeof InterfaceSound]): void {
    this.mixer.play(hook, SoundActor.Own);
  }

  /**
   * Maintenance has started: all music, ambience and effects stop and nothing else sounds, until `maintenanceEnd`.
   * With the cue, the maintenance sound plays alone. The countdown warnings before it are not part of this; each
   * plays the same sound over the world as it is.
   *
   * @param cue Whether the maintenance sound plays; false for a page that opened on the maintenance page, which
   *   holds the sound without the cue.
   * @returns True when the maintenance sound started.
   */
  public maintenanceStart(cue = true): boolean {
    if (!cue) {
      this.mixer.holdAlone();
      return false;
    }
    return this.mixer.playAlone(InterfaceSound.Maintenance, SoundActor.Own);
  }

  /**
   * Maintenance is over (the world is shown again, or the maintenance page sees the server open): the music resumes and every sound plays again.
   */
  public maintenanceEnd(): void {
    this.mixer.endAlone();
  }

  /**
   * The sample the Voice choice previews for a voice (P1.9-T83): the voice's own hurt, else its own death, else the
   * shared hurt, else the shared death, so a voice with one recording plays that recording, never the shared sound in
   * its place.
   *
   * @param voice The voice, or null for none.
   * @returns The hook to play, or null when none of them has a file.
   */
  public voiceSample(voice: string | null): string | null {
    const own = voice === null ? [] : [playerCombatHook(voice, "hurt"), playerCombatHook(voice, "death")];
    return [...own, PlayerSound.Hurt, PlayerSound.Death].find((hook) => this.bank.holds(hook)) ?? null;
  }

  /**
   * Plays a voice's sample on the page's own channel, as the Voice choice's Preview asks.
   *
   * @param voice The voice, or null for none.
   * @returns True when a sample started; false when the voice has none, or the one it has did not start (not yet
   *   decoded, or the context not running), so the caller says so rather than leave Preview silent.
   */
  public previewVoice(voice: string | null): boolean {
    const hook = this.voiceSample(voice);
    return hook !== null && this.mixer.play(hook, SoundActor.Own);
  }

  /**
   * Sounds a window opening or closing as the game menu moves between its surfaces: one more deeply nested opens, one
   * less closes.
   *
   * @param before The surface shown before: `closed`, `menu` or `settings`.
   * @param after The surface shown now.
   */
  public windowMoved(before: string, after: string): void {
    const from = WINDOW_DEPTH.indexOf(before);
    const to = WINDOW_DEPTH.indexOf(after);
    if (from !== to && from >= 0 && to >= 0) {
      this.ui(to > from ? InterfaceSound.Open : InterfaceSound.Close);
    }
  }

  /**
   * Wires the page: the first pointer or key press unlocks, the document's visibility drives the background mute, and
   * a press on a control clicks.
   *
   * @param target The window.
   * @param doc The document.
   */
  public attach(target: Window, doc: Document): void {
    const unlock = (): void => this.mixer.unlock();
    target.addEventListener("pointerdown", unlock, { capture: true });
    target.addEventListener("keydown", unlock, { capture: true });
    doc.addEventListener("visibilitychange", () => this.mixer.setHidden(doc.visibilityState === "hidden"));
    doc.addEventListener(
      "click",
      (event) => {
        if (event.target instanceof Element && event.target.closest(CLICKABLE) !== null) {
          this.ui(InterfaceSound.Click);
        }
      },
      { capture: true },
    );
  }
}

/**
 * The game page's sound, made from the browser.
 */
export const pageSound = new PageSound({
  context: browserContext,
  media: browserMedia,
  canPlay: browserCanPlay,
  fetchBytes: async (url) => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`${url} answered ${response.status}`);
    }
    return response.arrayBuffer();
  },
  fetchJson: async (url) => {
    const response = await fetch(url, { cache: "no-cache" });
    if (!response.ok) {
      throw new Error(`${url} answered ${response.status}`);
    }
    return response.json();
  },
  random: Math.random,
  settings: readAudioSettings(),
  keep: { level: (id, level) => void writeLevel(id, level), background: (mute) => void writeBackgroundMute(mute), whispers: (through) => void writeWhisperBackground(through) },
});
